import { DoctorLayout } from "@/components/layout/doctor-layout";
import { Link } from "wouter";
import { ArrowLeft, Briefcase } from "lucide-react";
import { Button } from "@/components/ui/button";

const SECTIONS = [
  {
    title: "National Consultant Selection Process",
    content: `Consultant posts in NHS rheumatology are advertised nationally on NHS Jobs and typically via BSR email list, EULAR notices, and rheumatology listservs. The selection process is standardised across Trusts, though the format varies.

A typical selection process includes:
• Shortlisting based on person specification (essential and desirable criteria)
• Presentation — usually 10-15 minutes on a clinical, leadership, or service topic, followed by 5 minutes of questions
• Panel interview — usually 30-45 minutes covering clinical expertise, leadership, governance, research, and values
• Some trusts include a medical scoring assessment (HAQ equivalent) or written component

Applications require a strong CV demonstrating clinical experience (case mix, procedures, clinics), leadership and management roles, research output, teaching, and audit/QIP. Tailor your covering statement carefully to the person specification and the Trust's strategic priorities.`,
  },
  {
    title: "Job Planning: PAs and SPAs",
    content: `NHS consultant contracts are measured in Programmed Activities (PAs). A full-time consultant typically has 10 PAs per week:

• Direct Clinical Care (DCC) PAs: clinics, ward rounds, procedures, MDTs, urgent referrals
• Supporting Professional Activities (SPA) PAs: CPD, audit, teaching, appraisal preparation, management, research

The BMA recommends a minimum of 1.5 SPAs (1.5 PAs/week) for a full-time consultant. This should cover CPD (including BSR/EULAR/NICE guideline reading), clinical audit participation, preparation for appraisal, supervision of trainees, and governance commitments.

Job planning is a formal annual process negotiated with your clinical lead and manager. It should be linked to the service's annual plan, your personal development plan, and your appraisal. Ensure your job plan is signed off and reviewed — it forms a formal contractual agreement.

Additional PAs can be offered for on-call, additional clinics, management roles, or research time. Negotiate these carefully, ideally with BMA advice, especially in your first consultant post.`,
  },
  {
    title: "Settling into Your First Consultant Post",
    content: `The transition from senior registrar to consultant is one of the most significant career steps. Key practical steps:

• Introduce yourself to all allied health professionals (specialist nurses, physiotherapists, OTs, pharmacists) and primary care leads in the first weeks
• Review the service's waiting times, referral pathways, and any outstanding audit or governance actions
• Attend your first MDT meetings as a contributor, not just an observer
• Register with your Medical Director's office for job planning and appraisal
• Establish your secretary and clinical admin team relationship early — this is crucial for your clinic efficiency
• Set up your diagnostic and prescribing access on Trust systems promptly

Imposter syndrome is common in the first 6-12 months. Most new consultants report feeling more settled after their first full annual appraisal cycle. Build a network of peer support among new consultants in your region and nationally through BSR.`,
  },
  {
    title: "Interview Preparation",
    content: `NHS consultant interviews are competency-based and use structured scoring. The 8 leadership domains used in NHS consultant selection (NHS Leadership Academy) form the framework for most interview questions. These are:

1. Inspiring shared purpose
2. Leading with care  
3. Evaluating information
4. Connecting our service
5. Sharing the vision
6. Engaging the team
7. Holding to account
8. Developing capability

Use the STAR (Situation, Task, Action, Result) framework for behavioural questions. For each domain, prepare 2-3 examples from your registrar career. Good examples come from QIPs, audit, teaching innovations, service redesign, leadership roles, and difficult patient care scenarios.

Visit the RheumHub Consultant Interview Prep tool for structured mock questions across all 8 domains.`,
  },
];

export default function ConsultantReadiness() {
  return (
    <DoctorLayout title="Consultant Readiness">
      <div className="space-y-6">
        <div className="flex items-center gap-3">
          <Link href="/doctor/career">
            <Button variant="ghost" size="sm" className="gap-1 text-slate-500">
              <ArrowLeft className="h-4 w-4" />
              Career Pathways
            </Button>
          </Link>
        </div>

        <div className="bg-gradient-to-br from-indigo-800 to-indigo-950 rounded-2xl p-6 text-white">
          <div className="flex items-center gap-3 mb-3">
            <Briefcase className="h-8 w-8 text-indigo-300" />
            <div>
              <h2 className="text-xl font-bold">Consultant Readiness</h2>
              <p className="text-indigo-300 text-sm">National selection · Job planning · Making the transition</p>
            </div>
          </div>
          <p className="text-indigo-200 text-sm leading-relaxed">
            Becoming a rheumatology consultant in the NHS is a significant step. Understanding the selection process, negotiating your job plan, and making a strong start in post are all learnable skills. This guide covers what to expect and how to prepare.
          </p>
        </div>

        <div className="space-y-4">
          {SECTIONS.map((section) => (
            <div key={section.title} className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 space-y-3">
              <h3 className="text-lg font-bold text-slate-900">{section.title}</h3>
              <div className="space-y-3">
                {section.content.split("\n\n").map((para, i) => (
                  <p key={i} className="text-sm text-slate-600 leading-relaxed whitespace-pre-line">{para}</p>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="bg-indigo-50 border border-indigo-200 rounded-2xl p-5 flex items-center justify-between">
          <div>
            <p className="font-semibold text-slate-900 text-sm">Ready to practise for your interview?</p>
            <p className="text-xs text-slate-500">Use the Consultant Interview Prep tool for mock questions across all 8 NHS leadership domains.</p>
          </div>
          <Link href="/doctor/career/interview-prep">
            <button className="px-4 py-2 bg-indigo-700 text-white rounded-xl text-sm font-medium hover:bg-indigo-800 transition-colors shrink-0">
              Interview Prep →
            </button>
          </Link>
        </div>
      </div>
    </DoctorLayout>
  );
}
